'use client';

import { createClient } from '@/lib/supabase/client';
import { Student, Lecturer, Exam, ExamResult, StudyMaterial, ExamQuestion } from '@/lib/mockData';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    return /relation.*does not exist|column.*does not exist|function.*does not exist|syntax error|type.*does not exist/i.test(error.message);
  }
  return false;
}

function dbToStudent(row: any): Student {
  return {
    id: row.id,
    studentNumber: row.student_number,
    fullName: row.full_name,
    nrcId: row.nrc_id,
    email: row.email,
    phone: row.phone,
    courseId: row.course_id,
    categoryCode: row.category_code,
    intake: row.intake,
    className: row.class_name,
    paymentStatus: row.payment_status as 'pending' | 'verified',
    accessCode: row.access_code,
    accessGranted: row.access_granted,
    enrolledAt: row.enrolled_at,
    referralCode: row.referral_code || undefined,
  } as any;
}

function dbToLecturer(row: any): Lecturer {
  return {
    id: row.id,
    fullName: row.full_name,
    email: row.email,
    staffId: row.staff_id,
    assignedCourses: row.assigned_courses || [],
  };
}

function dbToMaterial(row: any): StudyMaterial {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    type: row.material_type as StudyMaterial['type'],
    courseId: row.course_id,
    uploadedBy: row.uploaded_by,
    uploadedAt: row.uploaded_at,
    fileUrl: row.file_url,
    videoEmbedId: row.video_embed_id || undefined,
    topic: row.topic,
  };
}

function dbToExam(row: any): Exam {
  const questions: ExamQuestion[] = Array.isArray(row.questions) ? row.questions : [];
  return {
    id: row.id,
    title: row.title,
    courseId: row.course_id,
    duration: row.duration_minutes,
    totalMarks: row.total_marks,
    passMark: row.pass_mark,
    questions,
    createdBy: row.created_by,
    publishedAt: row.published_at || row.created_at,
    status: row.exam_status as Exam['status'],
  };
}

function dbToResult(row: any): ExamResult {
  return {
    id: row.id,
    examId: row.exam_id,
    studentId: row.student_id,
    studentNumber: row.student_number,
    studentName: row.student_name,
    courseId: row.course_id,
    score: row.score,
    totalMarks: row.total_marks,
    percentage: row.percentage,
    passed: row.passed,
    submittedAt: row.submitted_at,
    answers: Array.isArray(row.answers) ? row.answers : [],
    certificationStatus: row.certification_status as ExamResult['certificationStatus'],
  };
}

// ─── Student Service ──────────────────────────────────────────────────────────

export const studentService = {
  async getAll(): Promise<Student[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .order('enrolled_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map(dbToStudent);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async getByAccessCode(accessCode: string): Promise<Student | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('access_code', accessCode.toUpperCase())
        .eq('payment_status', 'verified')
        .eq('access_granted', true)
        .maybeSingle();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? dbToStudent(data) : null;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async getByEmail(email: string): Promise<Student | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('email', email)
        .maybeSingle();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? dbToStudent(data) : null;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async create(student: Omit<Student, 'id'> & { referralCode?: string }): Promise<Student | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('students')
        .insert({
          student_number: student.studentNumber,
          full_name: student.fullName,
          nrc_id: student.nrcId,
          email: student.email,
          phone: student.phone,
          course_id: student.courseId,
          category_code: student.categoryCode,
          intake: student.intake,
          class_name: student.className,
          payment_status: student.paymentStatus,
          access_code: student.accessCode,
          access_granted: student.accessGranted,
          enrolled_at: student.enrolledAt,
          referral_code: (student as any).referralCode || null,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? dbToStudent(data) : null;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async updatePaymentStatus(studentId: string, status: 'pending' | 'verified', accessGranted: boolean): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('students')
        .update({ payment_status: status, access_granted: accessGranted })
        .eq('id', studentId);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },
};

// ─── Lecturer Service ─────────────────────────────────────────────────────────

export const lecturerService = {
  async getAll(): Promise<Lecturer[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('lecturers')
        .select('*')
        .order('full_name', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map(dbToLecturer);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async getByEmail(email: string): Promise<Lecturer | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('lecturers')
        .select('*')
        .eq('email', email)
        .maybeSingle();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? dbToLecturer(data) : null;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },
};

// ─── Study Materials Service ──────────────────────────────────────────────────

export const materialService = {
  async getByCourse(courseId: string): Promise<StudyMaterial[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('study_materials')
        .select('*')
        .eq('course_id', courseId)
        .order('uploaded_at', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map(dbToMaterial);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async create(material: Omit<StudyMaterial, 'id'>): Promise<StudyMaterial | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('study_materials')
        .insert({
          title: material.title,
          description: material.description,
          material_type: material.type,
          course_id: material.courseId,
          uploaded_by: material.uploadedBy,
          file_url: material.fileUrl || '#',
          video_embed_id: material.videoEmbedId || '',
          topic: material.topic,
          uploaded_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) {
        console.error('[materialService.create] DB error:', error.message, error);
        if (error.message?.includes('row-level security') || error.message?.includes('policy') || error.message?.includes('permission') || error.message?.includes('403')) {
          throw new Error('Database permission denied. Please contact the administrator.');
        }
        if (isSchemaError(error)) throw error;
        throw new Error(`Failed to save material: ${error.message}`);
      }
      return data ? dbToMaterial(data) : null;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      throw e;
    }
  },

  async delete(materialId: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('study_materials')
        .delete()
        .eq('id', materialId);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },
};

// ─── Exam Service ─────────────────────────────────────────────────────────────

export const examService = {
  async getByCourse(courseId: string): Promise<Exam[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('exams')
        .select('*')
        .eq('course_id', courseId)
        .eq('exam_status', 'published')
        .order('published_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map(dbToExam);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async getAll(): Promise<Exam[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('exams')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map(dbToExam);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async create(exam: Omit<Exam, 'id'>): Promise<Exam | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('exams')
        .insert({
          title: exam.title,
          course_id: exam.courseId,
          duration_minutes: exam.duration,
          total_marks: exam.totalMarks,
          pass_mark: exam.passMark,
          questions: exam.questions,
          created_by: exam.createdBy,
          exam_status: exam.status,
          published_at: exam.status === 'published' ? new Date().toISOString() : null,
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? dbToExam(data) : null;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async update(examId: string, updates: Partial<Exam>): Promise<boolean> {
    const supabase = createClient();
    const payload: any = {};
    if (updates.title !== undefined) payload.title = updates.title;
    if (updates.status !== undefined) {
      payload.exam_status = updates.status;
      if (updates.status === 'published') payload.published_at = new Date().toISOString();
    }
    if (updates.questions !== undefined) payload.questions = updates.questions;
    if (updates.duration !== undefined) payload.duration_minutes = updates.duration;
    if (updates.totalMarks !== undefined) payload.total_marks = updates.totalMarks;
    if (updates.passMark !== undefined) payload.pass_mark = updates.passMark;
    try {
      const { error } = await supabase.from('exams').update(payload).eq('id', examId);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },

  async delete(examId: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('exams')
        .delete()
        .eq('id', examId);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },
};

// ─── Exam Results Service ─────────────────────────────────────────────────────

export const examResultService = {
  async getAll(): Promise<ExamResult[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('exam_results')
        .select('*')
        .order('submitted_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map(dbToResult);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async getByStudent(studentId: string): Promise<ExamResult[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('exam_results')
        .select('*')
        .eq('student_id', studentId)
        .order('submitted_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data || []).map(dbToResult);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async submit(result: Omit<ExamResult, 'id'>): Promise<ExamResult | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('exam_results')
        .insert({
          exam_id: result.examId,
          student_id: result.studentId,
          student_number: result.studentNumber,
          student_name: result.studentName,
          course_id: result.courseId,
          score: result.score,
          total_marks: result.totalMarks,
          percentage: result.percentage,
          passed: result.passed,
          answers: result.answers,
          certification_status: result.passed ? 'pending' : 'failed',
          submitted_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? dbToResult(data) : null;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async issueCertificate(resultId: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('exam_results')
        .update({ certification_status: 'issued' })
        .eq('id', resultId);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },
};
